import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  FaUser,
  FaFileAlt,
  FaSave,
  FaTimes,
  FaUpload,
  FaTrash,
  FaFilePdf,
} from "react-icons/fa";
import { jsPDF } from "jspdf";
import {
  createPatient,
  uploadPatientProfilePic,
  fetchPatientById,
  updatePatient,
  uploadPatientReferencePic,
  fetchPatientVideos,
  uploadPatientVideo,
  deleteVideoById,
  deletePatient,
} from "../../../api";
import {
  DEFAULT_PATIENT_PLACEHOLDER,
  clearImageCache,
} from "../../../utils/imageUtils";
import { getPatientPicHttpUrl, downloadPatientImage } from "../../../api";
import AuthenticatedImage from "../../AuthenticatedImage";
import { USER_ROLES } from "../../constants";
import Cookies from "js-cookie";
import "./ProfileDetail.css";
import "../../VideoDetectionList.css";
import {
  getVideoPlaybackUrl,
  updateVideoDiagnosis,
  updateVideoById,
} from "../../../api";

// Utility function to generate PDF report
const generatePDFReport = (video, patientName) => {
  const diagnosisLabels = {
    0: "None",
    1: "Low",
    2: "Middle",
    3: "High",
    4: "Positive",
    5: "Negative",
  };

  // Determine risk level based on diagnosis
  const getRiskLevel = (diagnosis) => {
    if (diagnosis === null || diagnosis === undefined) return "Unknown";
    // 0 = None, 1 = Low are considered Low ASD Risk
    // 2 = Middle is considered Medium ASD Risk
    // 3 = High,
    //  4 = Positive,
    //  5 = Negative are considered High ASD Risk
    if ([0, 1, 5].includes(diagnosis)) return "Low ASD Risk";
    if (diagnosis === 2) return "Medium ASD Risk";
    if (diagnosis === 4) return "Positive";
    if (diagnosis === 5) return "Negative";
    return "High ASD Risk";
  };

  const diagnosis = diagnosisLabels[video.diagnosis] || "Unknown";
  const riskLevel = getRiskLevel(video.diagnosis);
  const probability =
    video.diagnosis_probability !== null
      ? `${(video.diagnosis_probability * 100).toFixed(2)}%`
      : "N/A";
  const uploadedBy = video.uploaded_by || "Unknown";
  const createdAt = new Date(video.created_at).toLocaleString();
  const generatedAt = new Date().toLocaleString();

  // Create new PDF document
  const doc = new jsPDF();

  let yPos = 20;
  const pageWidth = doc.internal.pageSize.getWidth();
  const leftMargin = 20;
  const rightMargin = 20;
  const maxWidth = pageWidth - leftMargin - rightMargin;

  // Helper function to add text with word wrap
  const addText = (text, fontSize = 10, isBold = false, color = [0, 0, 0]) => {
    doc.setFontSize(fontSize);
    doc.setFont("helvetica", isBold ? "bold" : "normal");
    doc.setTextColor(...color);
    const lines = doc.splitTextToSize(text, maxWidth);
    lines.forEach((line) => {
      if (yPos > 270) {
        doc.addPage();
        yPos = 20;
      }
      doc.text(line, leftMargin, yPos);
      yPos += fontSize * 0.5;
    });
    yPos += 2;
  };

  const addLine = () => {
    doc.setDrawColor(200, 200, 200);
    doc.line(leftMargin, yPos, pageWidth - rightMargin, yPos);
    yPos += 5;
  };

  // Title
  addText("AI-ASD123 Video Analysis Report", 18, true, [239, 110, 77]);
  yPos += 3;
  addLine();
  yPos += 5;

  // Video Information Section
  addText("Video Information", 14, true, [45, 55, 72]);
  yPos += 2;
  addText(`Video Name: ${video.video_name}`, 10, false);
  addText(`Participant Name: ${patientName}`, 10, false);
  addText(`Uploaded By: ${uploadedBy}`, 10, false);
  addText(`Created At: ${createdAt}`, 10, false);
  yPos += 5;

  // Analysis Results Section
  addText("Analysis Results", 14, true, [45, 55, 72]);
  yPos += 2;

  addText(`AI Diagnosis: ${diagnosis}`, 10, true);
  // Doctor Diagnosis
  const doctorDiagnosisLabel =
    video.doctor_diagnosis !== undefined && video.doctor_diagnosis !== null
      ? diagnosisLabels[video.doctor_diagnosis] || video.doctor_diagnosis
      : "N/A";
  addText(`Doctor Diagnosis: ${doctorDiagnosisLabel}`, 10, false);
  // CRAS score
  const scoreValue =
    video.score !== undefined && video.score !== null ? video.score : "N/A";
  addText(`CRAS score: ${scoreValue}`, 10, false);

  // Color code risk level
  let riskColor = [45, 55, 72]; // default dark gray
  if (riskLevel === "Low ASD Risk")
    riskColor = [34, 139, 34]; // green
  else if (riskLevel === "Medium ASD Risk")
    riskColor = [255, 140, 0]; // orange
  else if (riskLevel === "High ASD Risk") riskColor = [220, 53, 69]; // red

  doc.setTextColor(...riskColor);
  addText(`Risk Level: ${riskLevel}`, 10, true, riskColor);
  doc.setTextColor(0, 0, 0);

  addText(`Diagnosis Probability: ${probability}`, 10, false);

  yPos += 5;

  // Report Summary Section
  addText("Report Summary", 14, true, [45, 55, 72]);
  yPos += 2;
  const summary = `${video.video_name} of ${patientName} uploaded by ${uploadedBy} on ${createdAt} shows a ${diagnosis} risk with diagnosis probability ${probability}.`;
  addText(summary, 10, false);
  yPos += 3;
  addText(`Risk Classification: ${riskLevel}`, 10, true, riskColor);
  yPos += 8;

  // Recommendations Table
  addText("Recommendations Table", 14, true, [45, 55, 72]);
  yPos += 5;

  // Table header
  doc.setFillColor(248, 250, 252);
  doc.rect(leftMargin, yPos - 5, maxWidth, 8, "F");
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text("Risk Level", leftMargin + 5, yPos);
  doc.text("Recommended Actions", leftMargin + 60, yPos);
  yPos += 5;
  addLine();

  // Low ASD Risk recommendations
  doc.setFont("helvetica", "bold");
  doc.text("Low ASD Risk", leftMargin + 5, yPos);
  doc.setFont("helvetica", "normal");
  yPos += 5;
  const lowRiskActions = [
    "• Continue regular developmental monitoring",
    "• Schedule routine follow-up assessments",
    "• Maintain communication with healthcare provider",
  ];
  lowRiskActions.forEach((action) => {
    addText(action, 9, false);
  });
  yPos += 3;
  addLine();

  // Medium ASD Risk recommendations
  doc.setFont("helvetica", "bold");
  doc.text("Medium ASD Risk", leftMargin + 5, yPos);
  doc.setFont("helvetica", "normal");
  yPos += 5;
  const mediumRiskActions = [
    "• Schedule comprehensive developmental screening",
    "• Consult with pediatric specialist",
    "• Monitor behavioral and social development closely",
    "• Consider early intervention assessment",
  ];
  mediumRiskActions.forEach((action) => {
    addText(action, 9, false);
  });
  yPos += 3;
  addLine();

  // High ASD Risk recommendations
  doc.setFont("helvetica", "bold");
  doc.text("High ASD Risk", leftMargin + 5, yPos);
  doc.setFont("helvetica", "normal");
  yPos += 5;
  const highRiskActions = [
    "• Immediate consultation with specialist",
    "• Comprehensive developmental evaluation",
    "• Consider early intervention programs",
    "• Schedule detailed clinical assessment",
  ];
  highRiskActions.forEach((action) => {
    addText(action, 9, false);
  });
  yPos += 8;

  // Disclaimer Section
  if (yPos > 200) {
    doc.addPage();
    yPos = 20;
  }

  addText("Important Disclaimer", 14, true, [146, 64, 14]);
  yPos += 2;

  // Draw background rectangle first
  const disclaimerStartY = yPos - 3;
  doc.setFillColor(255, 251, 235);

  const disclaimerText = [
    "AI-ASD123 is a health information management tool that uses AI to process, summarise and organise health-related information. It does not provide medical advice, diagnosis, or treatment, and its outputs may be incomplete or inaccurate. Do not use AI-ASD123 to make medical decisions or replace a qualified healthcare professional's judgment.",
    "",
    "This report is for reference only. If you have concerns regarding your child's development or cognitive function, please contact your clinician in charge.",
    "",
    "Development in children is a continuous process. As a child grows and develops, different aspects of him or her are changing continuously. Therefore, the information and recommendations contained in this report are meant for current use. Reference to or use of this report in the future should be made with considerable caution and reservation.",
  ];

  // Calculate disclaimer height first
  let tempYPos = yPos;
  disclaimerText.forEach((paragraph) => {
    if (paragraph === "") {
      tempYPos += 3;
    } else {
      const lines = doc.splitTextToSize(paragraph, maxWidth - 4);
      tempYPos += lines.length * 4.5 + 2;
    }
  });

  const disclaimerHeight = tempYPos - disclaimerStartY + 3;

  // Draw background rectangle
  doc.rect(
    leftMargin - 2,
    disclaimerStartY,
    maxWidth + 4,
    disclaimerHeight,
    "F",
  );

  // Add disclaimer text on top of background
  yPos = disclaimerStartY + 5;
  disclaimerText.forEach((paragraph) => {
    if (paragraph === "") {
      yPos += 3;
    } else {
      const lines = doc.splitTextToSize(paragraph, maxWidth - 4);
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(120, 53, 15);
      lines.forEach((line) => {
        doc.text(line, leftMargin + 2, yPos);
        yPos += 4.5;
      });
      yPos += 2;
    }
  });

  // Footer
  yPos += 5;
  addLine();
  addText(`Generated: ${generatedAt}`, 8, false, [113, 128, 150]);

  // Save the PDF
  const fileName = `ASD_Report_${video.video_name.replace(/\.[^/.]+$/, "")}_${new Date().getTime()}.pdf`;
  doc.save(fileName);
};

// Constants
const TABS = [
  { id: "personal", label: "Personal Information", icon: FaUser },
  { id: "photos", label: "Reference Photos", icon: FaFileAlt },
  { id: "videos", label: "Video List", icon: FaFileAlt },
];

const getTodayDateString = () => new Date().toISOString().split("T")[0];

const isRawVideoUnavailable = (video) => Boolean(video && (
  video.raw_video_state && video.raw_video_state !== "available"
));

const INITIAL_FORM_DATA = {
  patient_name: "",
  patient_email: "",
  patient_phone: "",
  patient_dob: getTodayDateString(),
  patient_profile_pic: null,
  patient_reference_pics: [],
};

const VALIDATION_RULES = {
  patient_name: { required: true, minLength: 2, maxLength: 100 },
  patient_email: { required: false, pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/ },
  patient_phone: { required: false, pattern: /^[\+]?[\d\s\-\(\)\.]{6,20}$/ },
  patient_dob: { required: false },
};

// Utility Functions
const formatDateForInput = (dateString) => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    return date.toISOString().split("T")[0];
  } catch (error) {
    console.error("Error formatting date for input:", error);
    return "";
  }
};

const formatDateForAPI = (dateString) => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString + "T00:00:00");
    return date.toISOString();
  } catch (error) {
    console.error("Error formatting date for API:", error);
    return dateString;
  }
};

const generateFileId = (file, index = 0) => {
  if (file instanceof File) {
    return `${file.name}-${file.size}-${file.lastModified}-${index}`;
  } else if (typeof file === "string") {
    return `${file}-${index}`;
  }
  return `file-${Date.now()}-${index}`;
};

const useFormValidation = (formData) => {
  const [errors, setErrors] = useState({});

  const validateField = useCallback(
    (name, value) => {
      const rule = VALIDATION_RULES[name];
      if (!rule) return true;

      let isValid = true;
      let errorMessage = null;

      if (rule.required && !value?.toString().trim()) {
        errorMessage = "This field is required";
        isValid = false;
      } else if (
        value &&
        rule.pattern &&
        !rule.pattern.test(value.toString().trim())
      ) {
        errorMessage = getPatternErrorMessage(name);
        isValid = false;
      } else if (
        value &&
        rule.minLength &&
        value.toString().trim().length < rule.minLength
      ) {
        errorMessage = `Must be at least ${rule.minLength} characters`;
        isValid = false;
      } else if (
        value &&
        rule.maxLength &&
        value.toString().trim().length > rule.maxLength
      ) {
        errorMessage = `Must be no more than ${rule.maxLength} characters`;
        isValid = false;
      }

      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        if (errorMessage) {
          newErrors[name] = errorMessage;
        } else {
          delete newErrors[name];
        }
        return newErrors;
      });
      return isValid;
    },
    [], // no dependency on errors — uses functional setState
  );

  const getPatternErrorMessage = (fieldName) => {
    const messages = {
      patient_email: "Please enter a valid email address",
      patient_phone: "Please enter a valid phone number",
    };
    return messages[fieldName] || "Invalid format";
  };

  const validateAll = useCallback(() => {
    const requiredFields = Object.keys(VALIDATION_RULES).filter(
      (key) => VALIDATION_RULES[key].required && key !== "identification",
    );

    let isValid = true;

    // Validate regular required fields
    requiredFields.forEach((field) => {
      if (!validateField(field, formData[field] || "")) {
        isValid = false;
      }
    });

    return isValid;
  }, [formData, validateField]);

  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  return {
    errors,
    validateField,
    validateAll,
    clearErrors,
    setErrors, // Add setErrors to the return object
  };
};

const useFormData = (initialData = INITIAL_FORM_DATA) => {
  const [formData, setFormData] = useState(initialData);
  const [hasChanges, setHasChanges] = useState(false);

  const handleChange = useCallback((e) => {
    const { name, value, files } = e.target;
    if (files) {
      if (name === "patient_profile_pic") {
        setFormData((prev) => ({ ...prev, [name]: files[0] }));
      } else if (name === "patient_reference_pics") {
        setFormData((prev) => ({
          ...prev,
          [name]: [...prev[name], ...Array.from(files)],
        }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    setHasChanges(true);
  }, []);

  const handleFileRemove = useCallback((fieldName, index = null) => {
    if (fieldName === "patient_profile_pic") {
      setFormData((prev) => ({ ...prev, [fieldName]: null }));
    } else if (fieldName === "patient_reference_pics" && index !== null) {
      setFormData((prev) => ({
        ...prev,
        [fieldName]: prev[fieldName].filter((_, i) => i !== index),
      }));
    }
    setHasChanges(true);
  }, []);

  const resetForm = useCallback(() => {
    setFormData(initialData);
    setHasChanges(false);
  }, [initialData]);

  const setFormDataDirectly = useCallback((newData) => {
    setFormData(newData);
    setHasChanges(false);
  }, []);

  return {
    formData,
    handleChange,
    handleFileRemove,
    resetForm,
    setFormDataDirectly,
    hasChanges,
  };
};

// UI Components
const TabNavigation = ({ activeTab, onTabChange }) => (
  <div className="row-view">
    {TABS.map((tab) => {
      const IconComponent = tab.icon;
      const isActive = activeTab === tab.id;
      return (
        <div
          key={tab.id}
          className={`button ${isActive ? "button-active" : ""}`}
          onClick={() => onTabChange(tab.id)}
        >
          <div className="row-view2">
            <IconComponent className="image" />
            <span className="text3">{tab.label}</span>
          </div>
        </div>
      );
    })}
  </div>
);

const InputField = ({
  label,
  name,
  value = "",
  onChange,
  placeholder,
  type = "text",
  error,
  required = false,
  className = "",
  disabled = false,
  rows,
  ...props
}) => {
  const isTextArea = type === "textarea";
  const InputComponent = isTextArea ? "textarea" : "input";
  return (
    <div className={`column ${className}`}>
      {label && (
        <div className="text2">
          {label} {required && <span className="required-text">*</span>}
        </div>
      )}
      <InputComponent
        type={isTextArea ? undefined : type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`input ${error ? "input-error" : ""} ${
          disabled ? "input-disabled" : ""
        }`}
        required={required}
        disabled={disabled}
        rows={rows}
        {...props}
      />
      {error && <div className="error-text">{error}</div>}
    </div>
  );
};

const DateField = ({
  label,
  name,
  value = "",
  onChange,
  error,
  required = false,
  disabled = false,
  className = "",
  ...props
}) => (
  <div className={`column ${className}`}>
    {label && (
      <div className="text2">
        {label} {required && <span className="required-text">*</span>}
      </div>
    )}
    <div className="row-view3">
      <input
        type="date"
        name={name}
        value={value}
        onChange={onChange}
        className={`input ${error ? "input-error" : ""} ${
          disabled ? "input-disabled" : ""
        }`}
        required={required}
        disabled={disabled}
        style={{ width: "100%" }}
        {...props}
      />
    </div>
    {error && <div className="error-text">{error}</div>}
  </div>
);

const FileUploadField = ({
  label,
  name,
  onChange,
  accept = "image/*",
  error,
  required = false,
  multiple = false,
  className = "",
}) => (
  <div className={`column ${className}`}>
    <div className="button2">
      <input
        type="file"
        name={name}
        onChange={onChange}
        accept={accept}
        multiple={multiple}
        required={required}
        style={{ display: "none" }}
        id={`file-${name}`}
      />
      <label htmlFor={`file-${name}`} className="button3">
        <FaUpload className="image" />
        <span className="text3">
          {label || `Choose ${multiple ? "Files" : "File"}`}
        </span>
      </label>
    </div>
    {error && <div className="error-text">{error}</div>}
  </div>
);

const FilePreview = ({
  file,
  onRemove,
  isProfilePic = false,
  patientId = null,
  isExisting = false,
  fileIndex = 0,
  forceRefresh = 0,
}) => {
  const [localPreview, setLocalPreview] = useState(null);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    if (file instanceof File) {
      const objectUrl = URL.createObjectURL(file);
      setLocalPreview(objectUrl);
      setImageError(false);
      return () => {
        URL.revokeObjectURL(objectUrl);
      };
    } else {
      setLocalPreview(null);
      setImageError(false);
    }
  }, [file]);

  const getFileName = () => {
    if (file instanceof File) {
      return file.name;
    } else if (typeof file === "string") {
      const pathParts = file.split("/");
      return pathParts[pathParts.length - 1] || "Participant Image";
    }
    return isExisting ? "Existing Participant Image" : "Participant Image";
  };

  const handleImageError = useCallback((error) => {
    setImageError(true);
  }, []);

  // Download handler (must be outside of any condition)
  const handleDownload = useCallback(async () => {
    if (file instanceof File) {
      const url = URL.createObjectURL(file);
      const a = document.createElement("a");
      a.href = url;
      a.download = getFileName();
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);
    } else if (typeof file === "string") {
      try {
        await downloadPatientImage(patientId, file);
      } catch (err) {
        alert("Failed to download image.");
      }
    }
  }, [file, patientId]);

  if (isProfilePic) {
    return (
      <div className="profile-pic-preview">
        {file instanceof File ? (
          <img
            src={localPreview || DEFAULT_PATIENT_PLACEHOLDER}
            alt="Profile"
            className="profile-pic-image"
            onError={(e) => {
              e.target.src = DEFAULT_PATIENT_PLACEHOLDER;
              setImageError(true);
            }}
          />
        ) : imageError ? (
          <img
            src={DEFAULT_PATIENT_PLACEHOLDER}
            alt="Profile"
            className="profile-pic-image"
          />
        ) : (
          <AuthenticatedImage
            patientId={patientId}
            alt="Profile"
            circle={true}
            size={76}
            borderWidth={1}
            borderColor="#dee2e6"
            onError={handleImageError}
          />
        )}
      </div>
    );
  }

  return (
    <div className="contain reference-pic">
      <div className="reference-pic-image-container">
        {file instanceof File ? (
          <img
            src={localPreview || DEFAULT_PATIENT_PLACEHOLDER}
            alt="Reference"
            className="reference-pic-image"
            onError={(e) => {
              e.target.src = DEFAULT_PATIENT_PLACEHOLDER;
              setImageError(true);
            }}
          />
        ) : imageError ? (
          <img
            src={DEFAULT_PATIENT_PLACEHOLDER}
            alt="Reference"
            className="reference-pic-image"
          />
        ) : (
          <AuthenticatedImage
            patientId={patientId}
            imagePath={file}
            alt="Reference"
            className="reference-pic-image"
            onError={handleImageError}
          />
        )}
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: "8px",
          alignItems: "center",
          marginTop: "4px",
        }}
      >
        <div className="button-remove" onClick={onRemove} title="Remove file">
          <FaTrash />
        </div>
        <div
          className="button-download"
          onClick={handleDownload}
          title="Download image"
          style={{ cursor: "pointer" }}
        >
          <FaFileAlt />
        </div>
      </div>
      <div className="text4">
        {getFileName()}
        {isExisting && <span className="existing-badge"> (Existing)</span>}
      </div>
    </div>
  );
};

const PersonalInformationForm = ({
  formData,
  onChange,
  onFileRemove,
  errors,
  patientId = null,
  forceRefresh = 0,
}) => (
  <div className="column2">
    <div className="profile-pic-section">
      <div className="profile-pic-container">
        <div
          className="profile-pic-circle"
          onClick={() =>
            document.getElementById("file-patient_profile_pic").click()
          }
          title="Click to upload profile picture"
          style={{ width: "80px", height: "80px" }}
        >
          {formData.patient_profile_pic ? (
            <FilePreview
              key={`profile-${generateFileId(
                formData.patient_profile_pic,
              )}-${forceRefresh}`}
              file={formData.patient_profile_pic}
              onRemove={() => onFileRemove("patient_profile_pic")}
              isProfilePic={true}
              patientId={patientId}
              isExisting={typeof formData.patient_profile_pic === "string"}
              fileIndex={0}
              forceRefresh={forceRefresh}
            />
          ) : (
            <div className="profile-pic-placeholder">
              <FaUser className="placeholder-icon" />
            </div>
          )}
        </div>

        {formData.patient_profile_pic && (
          <div
            className="profile-pic-remove"
            onClick={(e) => {
              e.stopPropagation();
              onFileRemove("patient_profile_pic");
            }}
            title="Remove photo"
            style={{
              position: "absolute",
              top: "-5px",
              right: "-5px",
              width: "20px",
              height: "20px",
              backgroundColor: "#dc3545",
              color: "white",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              fontSize: "12px",
              border: "2px solid white",
              boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
              zIndex: 10,
            }}
          >
            <FaTimes />
          </div>
        )}
        <input
          type="file"
          name="patient_profile_pic"
          onChange={onChange}
          accept="image/*"
          style={{ display: "none" }}
          id="file-patient_profile_pic"
        />
        {errors.patient_profile_pic && (
          <div className="error-text">{errors.patient_profile_pic}</div>
        )}
      </div>
    </div>

    <InputField
      label="Full Name"
      name="patient_name"
      value={formData.patient_name}
      onChange={onChange}
      placeholder="Enter participant's full name"
      error={errors.patient_name}
      required
      className="column3"
    />

    <div className="row-view4">
      <InputField
        label="Email Address"
        name="patient_email"
        value={formData.patient_email}
        onChange={onChange}
        placeholder="Enter email address"
        type="email"
        error={errors.patient_email}
        required={false}
        className="column4"
      />
      <InputField
        label="Phone Number"
        name="patient_phone"
        value={formData.patient_phone}
        onChange={onChange}
        placeholder="Enter phone number"
        type="tel"
        error={errors.patient_phone}
        required={false}
        className="column4"
      />
    </div>

    <DateField
      label="Date of Birth"
      name="patient_dob"
      value={formData.patient_dob}
      onChange={onChange}
      error={errors.patient_dob}
      required={false}
      className="column4"
    />
  </div>
);

const PhotosForm = ({
  formData,
  onChange,
  onFileRemove,
  errors,
  patientId = null,
  existingReferencePhotos = [],
  onExistingPhotoRemove,
  forceRefresh = 0,
}) => (
  <div className="column2">
    <div className="column5">
      <div className="text2">
        Upload additional reference photos to help with participant
        identification and records.
      </div>
      <FileUploadField
        name="patient_reference_pics"
        onChange={onChange}
        accept="image/*"
        multiple={true}
        error={errors.patient_reference_pics}
        className="column3"
        label="Choose Files"
      />
      {(formData.patient_reference_pics.length > 0 ||
        existingReferencePhotos.length > 0) && (
        <div className="row-view5">
          {existingReferencePhotos.map((photo, index) => (
            <FilePreview
              key={`existing-${index}-${generateFileId(
                photo,
                index,
              )}-${forceRefresh}`}
              file={photo}
              onRemove={() => {
                if (onExistingPhotoRemove) {
                  onExistingPhotoRemove(index, photo);
                }
              }}
              isProfilePic={false}
              patientId={patientId}
              isExisting={true}
              fileIndex={index}
              forceRefresh={forceRefresh}
            />
          ))}
          {formData.patient_reference_pics.map((file, index) => (
            <FilePreview
              key={`new-${index}-${generateFileId(file, index)}`}
              file={file}
              onRemove={() => onFileRemove("patient_reference_pics", index)}
              isProfilePic={false}
              patientId={patientId}
              isExisting={false}
              fileIndex={index}
            />
          ))}
        </div>
      )}
      {existingReferencePhotos.length === 0 &&
        formData.patient_reference_pics.length === 0 && (
          <div className="no-photos-message">
            <p className="text2">
              No reference photos added yet. Upload some photos to help with
              participant identification.
            </p>
          </div>
        )}
    </div>
  </div>
);

const DeleteConfirmModal = ({ onConfirm, onCancel, isDeleting }) => (
  <div className="modal-overlay">
    <div className="modal-box">
      <h3 className="modal-title">Delete Participant</h3>
      <p className="modal-body">
        Are you sure you want to permanently delete this participant? This
        action
        <strong> cannot be undone</strong>.
      </p>
      <div className="modal-actions">
        <div
          className="button2"
          onClick={onCancel}
          style={{
            opacity: isDeleting ? 0.5 : 1,
            pointerEvents: isDeleting ? "none" : "auto",
          }}
        >
          <FaTimes className="image" />
          <span className="text3">Cancel</span>
        </div>
        <div
          className="button-delete"
          onClick={!isDeleting ? onConfirm : undefined}
          style={{
            opacity: isDeleting ? 0.5 : 1,
            pointerEvents: isDeleting ? "none" : "auto",
          }}
        >
          <FaTrash className="image" />
          <span className="text3">
            {isDeleting ? "Deleting..." : "Yes, Delete"}
          </span>
        </div>
      </div>
    </div>
  </div>
);

const ActionButtons = ({
  onSave,
  onCancel,
  onDelete,
  hasChanges,
  isSubmitting,
  mode = "create",
  isFormValid = true,
}) => (
  <div className="button-row-view">
    <div
      className="button2"
      onClick={onCancel}
      style={{
        opacity: isSubmitting ? 0.5 : 1,
        pointerEvents: isSubmitting ? "none" : "auto",
      }}
    >
      <FaTimes className="image" />
      <span className="text3">Cancel</span>
    </div>
    {mode === "edit" && onDelete && (
      <div
        className="button-delete"
        onClick={!isSubmitting ? onDelete : undefined}
        style={{
          opacity: isSubmitting ? 0.5 : 1,
          pointerEvents: isSubmitting ? "none" : "auto",
        }}
      >
        <FaTrash className="image" />
        <span className="text3">Delete</span>
      </div>
    )}
    <div
      className={`button3 ${
        !isFormValid || isSubmitting ? "button-disabled" : ""
      }`}
      onClick={!isFormValid || isSubmitting ? undefined : onSave}
      style={{ opacity: !isFormValid || isSubmitting ? 0.5 : 1 }}
    >
      <FaSave className="image" />
      <span className="text3">
        {isSubmitting
          ? "Saving..."
          : mode === "create"
            ? "Create Participant"
            : "Save Changes"}
      </span>
    </div>
  </div>
);

const SuccessMessage = ({ message, onClose }) => (
  <div className="success-message">
    <span>{message}</span>
    <FaTimes className="close-icon" onClick={onClose} />
  </div>
);

const ErrorMessage = ({ message, onClose }) => (
  <div className="error-message">
    <span>{message}</span>
    <FaTimes className="close-icon" onClick={onClose} />
  </div>
);

// VideoListForm component
const VideoListForm = ({ patientId, patientData, formData }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [allowForRetraining, setAllowForRetraining] = useState(false);
  // ...inside VideoListForm component...
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoError, setVideoError] = useState("");
  const [playbackUnavailable, setPlaybackUnavailable] = useState(false);
  const selectedRawUnavailable = playbackUnavailable || isRawVideoUnavailable(selectedVideo);
  const videoElementRef = useRef(null);
  const playbackRefreshTimerRef = useRef(null);
  const playbackRequestSequenceRef = useRef(0);
  const playbackErrorRetryCountRef = useRef(0);
  const PLAYBACK_REFRESH_BUFFER_MS = 30_000;
  const MAX_PLAYBACK_ERROR_RETRIES = 1;

  const [selectedSeverity, setSelectedSeverity] = useState("");
  const [score, setScore] = useState("");
  const [isSubmittingDiagnosis, setIsSubmittingDiagnosis] = useState(false);
  const [diagnosisError, setDiagnosisError] = useState("");
  const [diagnosisSuccess, setDiagnosisSuccess] = useState("");
  const [allowForRetrainingDetail, setAllowForRetrainingDetail] =
    useState(false);
  const [initialAllowForRetrainingDetail, setInitialAllowForRetrainingDetail] =
    useState(false);

  // Get user role from cookies
  const userRole = Cookies.get("platform_role");
  const canEditSeverity =
    userRole === USER_ROLES.ADMIN ||
    userRole === USER_ROLES.ITDOG ||
    userRole === USER_ROLES.DOCTOR;

  // Severity options
  const severityOptions = [
    { value: "0", label: "None" },
    { value: "1", label: "Low" },
    { value: "2", label: "Middle" },
    { value: "3", label: "High" },
  ];

  const clearPlaybackRefreshTimer = () => {
    if (playbackRefreshTimerRef.current) {
      clearTimeout(playbackRefreshTimerRef.current);
      playbackRefreshTimerRef.current = null;
    }
  };

  const applyVideoSource = (nextUrl, preserveState = true) => {
    const videoElement = videoElementRef.current;
    if (!videoElement || !preserveState || !videoElement.src) {
      setVideoUrl(nextUrl);
      return;
    }

    const snapshot = {
      currentTime: Number.isFinite(videoElement.currentTime)
        ? videoElement.currentTime
        : 0,
      playbackRate: Number.isFinite(videoElement.playbackRate)
        ? videoElement.playbackRate
        : 1,
      shouldResume: !videoElement.paused && !videoElement.ended,
    };

    const restorePlaybackState = () => {
      if (!videoElementRef.current) {
        return;
      }
      if (Number.isFinite(snapshot.currentTime) && snapshot.currentTime >= 0) {
        videoElementRef.current.currentTime = snapshot.currentTime;
      }
      videoElementRef.current.playbackRate = snapshot.playbackRate;
      if (snapshot.shouldResume) {
        videoElementRef.current.play().catch(() => {
          return null;
        });
      }
    };

    videoElement.addEventListener("loadedmetadata", restorePlaybackState, {
      once: true,
    });
    setVideoUrl(nextUrl);
  };

  const requestPlaybackUrl = async (
    videoId,
    { preserveState = true, showLoading = false } = {},
  ) => {
    const video = videos.find((item) => item.video_id === videoId) || selectedVideo;
    if (isRawVideoUnavailable(video)) {
      clearPlaybackRefreshTimer();
      setVideoUrl(null);
      setVideoLoading(false);
      setPlaybackUnavailable(true);
      setVideoError("Raw video has expired. Analysis results remain available.");
      return;
    }
    const requestId = ++playbackRequestSequenceRef.current;
    if (showLoading) {
      setVideoLoading(true);
    }

    try {
      const playback = await getVideoPlaybackUrl(videoId);
      if (requestId !== playbackRequestSequenceRef.current) {
        return;
      }
      setVideoError("");
      playbackErrorRetryCountRef.current = 0;
      applyVideoSource(playback.url, preserveState);

      clearPlaybackRefreshTimer();
      const expiresAtMs = Date.parse(playback.expires_at || "");
      if (Number.isFinite(expiresAtMs)) {
        const refreshDelayMs = Math.max(
          0,
          expiresAtMs - Date.now() - PLAYBACK_REFRESH_BUFFER_MS,
        );
        playbackRefreshTimerRef.current = setTimeout(() => {
          requestPlaybackUrl(videoId, {
            preserveState: true,
            showLoading: false,
          });
        }, refreshDelayMs);
      }
    } catch (error) {
      if (requestId !== playbackRequestSequenceRef.current) {
        return;
      }
      const statusCode = error?.response?.status;
      if (statusCode === 401 || statusCode === 403) {
        setVideoError("You are not authorized to view this video.");
      } else if (statusCode === 404) {
        clearPlaybackRefreshTimer();
        setVideoUrl(null);
        setPlaybackUnavailable(true);
        setVideoError("Raw video is no longer available. Analysis results remain available.");
      } else {
        setVideoError("Failed to load video. Please try again.");
      }
    } finally {
      if (requestId === playbackRequestSequenceRef.current) {
        setVideoLoading(false);
      }
    }
  };

  const handleVideoPlaybackError = () => {
    if (!selectedVideo || selectedRawUnavailable) {
      return;
    }
    if (playbackErrorRetryCountRef.current >= MAX_PLAYBACK_ERROR_RETRIES) {
      setVideoError("Playback failed. Please retry.");
      return;
    }
    playbackErrorRetryCountRef.current += 1;
    requestPlaybackUrl(selectedVideo.video_id, {
      preserveState: true,
      showLoading: false,
    });
  };

  const handleVideoView = async (video) => {
    try {
      playbackErrorRetryCountRef.current = 0;
      playbackRequestSequenceRef.current += 1;
      clearPlaybackRefreshTimer();
      setPlaybackUnavailable(false);
      setVideoUrl(null);
      setSelectedVideo(video);
      const retrainingValue = Boolean(video.allow_for_retraining);
      setAllowForRetrainingDetail(retrainingValue);
      setInitialAllowForRetrainingDetail(retrainingValue);
      setShowVideoModal(true);
      if (isRawVideoUnavailable(video)) {
        setVideoUrl(null);
        setVideoError("Raw video has expired. Analysis results remain available.");
      } else {
        setVideoError("");
        await requestPlaybackUrl(video.video_id, {
          preserveState: false,
          showLoading: true,
        });
      }
    } catch (error) {
      console.error("Error loading video:", error);
      setVideoError("Failed to load video. Please try again.");
    } finally {
      setVideoLoading(false);
    }
  };

  const handleCloseVideoModal = () => {
    playbackRequestSequenceRef.current += 1;
    playbackErrorRetryCountRef.current = 0;
    clearPlaybackRefreshTimer();
    setShowVideoModal(false);
    setSelectedVideo(null);
    setVideoUrl(null);
    setVideoError("");
    setVideoLoading(false);
    setPlaybackUnavailable(false);
    setAllowForRetrainingDetail(false);
    setInitialAllowForRetrainingDetail(false);
  };

  useEffect(() => {
    return () => {
      playbackRequestSequenceRef.current += 1;
      clearPlaybackRefreshTimer();
    };
  }, []);
  const handleSeverityChange = (value) => {
    setSelectedSeverity(value);
    setDiagnosisError("");
  };

  const handleSubmitDiagnosis = async () => {
    if (!selectedVideo) {
      setDiagnosisError("No video selected.");
      return;
    }

    const hasRetrainingChanged =
      !selectedRawUnavailable &&
      allowForRetrainingDetail !== initialAllowForRetrainingDetail;
    const hasDiagnosisUpdate = selectedSeverity !== "";

    if (!hasDiagnosisUpdate && !hasRetrainingChanged) {
      setDiagnosisError(
        "Please update diagnosis level or medical study permission before submitting.",
      );
      return;
    }

    if (
      hasDiagnosisUpdate &&
      (score === "" || isNaN(score) || score < 15 || score > 60)
    ) {
      setDiagnosisError("Please enter a valid CRAS score between 15 and 60.");
      return;
    }

    setIsSubmittingDiagnosis(true);
    setDiagnosisError("");
    setDiagnosisSuccess("");

    try {
      if (hasDiagnosisUpdate) {
        // Send both doctor_diagnosis and score
        await updateVideoDiagnosis(selectedVideo.video_id, {
          doctor_diagnosis: parseInt(selectedSeverity),
          score: Number(score),
        });
      }

      if (hasRetrainingChanged) {
        const updated = await updateVideoById(selectedVideo.video_id, {
          allow_for_retraining: allowForRetrainingDetail,
        });
        const nextValue = Boolean(
          updated?.allow_for_retraining ?? allowForRetrainingDetail,
        );
        setAllowForRetrainingDetail(nextValue);
        setInitialAllowForRetrainingDetail(nextValue);
      }

      setDiagnosisSuccess("Video updated successfully!");

      // Update the video in the local state
      try {
        const data = await fetchPatientVideos(patientId);
        setVideos(data.videos || []);
      } catch (refreshError) {
        console.error("Error refreshing video list:", refreshError);
        // Fallback to local state update if refresh fails
        setVideos((prevVideos) =>
          prevVideos.map((video) =>
            video.video_id === selectedVideo.video_id
              ? {
                  ...video,
                  ...(hasDiagnosisUpdate
                    ? {
                        doctor_diagnosis: parseInt(selectedSeverity),
                        score: Number(score),
                      }
                    : {}),
                  ...(hasRetrainingChanged
                    ? {
                        allow_for_retraining: allowForRetrainingDetail,
                      }
                    : {}),
                }
              : video,
          ),
        );
      }

      setSelectedVideo((prev) =>
        prev
          ? {
              ...prev,
              ...(hasDiagnosisUpdate
                ? {
                    doctor_diagnosis: parseInt(selectedSeverity),
                    score: Number(score),
                  }
                : {}),
              ...(hasRetrainingChanged
                ? { allow_for_retraining: allowForRetrainingDetail }
                : {}),
            }
          : prev,
      );

      // Close modal after a short delay
      setTimeout(() => {
        handleCloseVideoModal();
      }, 1500);
    } catch (error) {
      console.error("Error updating diagnosis:", error);
      setDiagnosisError("Failed to update diagnosis. Please try again.");
    } finally {
      setIsSubmittingDiagnosis(false);
    }
  };

  // ...rest of VideoListForm component...
  useEffect(() => {
    if (!patientId) return;
    setLoading(true);
    setError("");
    fetchPatientVideos(patientId)
      .then((data) => setVideos(data.videos || []))
      .catch(() => {
        setVideos([]);
      })
      .finally(() => setLoading(false));
  }, [patientId, uploadSuccess]);

  const handleAddVideo = () => {
    setShowUpload(true);
    setUploadError("");
    setUploadSuccess("");
    setSelectedFile(null);
    setAllowForRetraining(false);
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
    setUploadError("");
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadError("Please select a video file.");
      return;
    }
    setUploading(true);
    setUploadError("");
    try {
      await uploadPatientVideo(selectedFile, patientId, allowForRetraining);
      setUploadSuccess("Video uploaded successfully!");
      setShowUpload(false);
      setAllowForRetraining(false);
    } catch (err) {
      setUploadError("Failed to upload video.");
    } finally {
      setUploading(false);
    }
  };

  const handleCloseModal = () => {
    setShowUpload(false);
    setSelectedFile(null);
    setUploadError("");
    setAllowForRetraining(false);
  };

  // Handle video deletion (must be outside JSX)
  const handleDeleteVideo = async (video) => {
    if (
      !window.confirm(
        `Are you sure you want to delete the video: ${video.video_name}?`,
      )
    )
      return;
    try {
      setUploading(true);
      setUploadError("");
      await deleteVideoById(video.video_id);
      setUploadSuccess("Video deleted successfully!");
      // Refresh video list
      const data = await fetchPatientVideos(patientId);
      setVideos(data.videos || []);
    } catch (err) {
      setUploadError("Failed to delete video.");
    } finally {
      setUploading(false);
    }
  };

  if (!patientId) return <div className="text2">No participant selected.</div>;
  if (loading) return <div className="text2">Loading videos...</div>;
  if (error) return <div className="error-text">{error}</div>;
  const diagnosisLabels = {
    0: "None",
    1: "Low",
    2: "Middle",
    3: "High",
    4: "Positive",
    5: "Negative",
  };

  return (
    // ...inside VideoListForm, replace the table rendering with this...

    <div className="video-detection-container">
      <div className="video-table-container">
        <div
          className="row-view"
          style={{ justifyContent: "end", marginBottom: "16px" }}
        >
          <button className="add-video-button" onClick={handleAddVideo}>
            <FaUpload className="add-video-icon" />
            <span>Add Video</span>
          </button>
        </div>
        {uploadSuccess && (
          <div className="success-message">{uploadSuccess}</div>
        )}
        <div className="column5">
          {videos.length === 0 ? (
            <div className="text2">No videos found.</div>
          ) : (
            // ...inside VideoListForm component return statement...
            <div className="video-table">
              <div className="table-header-row">
                <span className="header-cell">Video Name</span>
                <span className="header-cell">Diagnosis</span>
                {/* <span className="header-cell">Diagnosis Probability</span> */}
                <span className="header-cell">Doctor Diagnosis</span>
                <span className="header-cell">Allow For Retraining</span>
                {/* <span className="header-cell">Score</span> */}
                <span className="header-cell">Status</span>
                <span className="header-cell">Created At</span>
                <span className="header-cell">Actions</span>
              </div>
              {videos.map((video) => (
                <div key={video.video_id} className="table-row">
                  <span className="table-cell video-name">
                    {video.video_name}
                  </span>
                  <span className="table-cell">
                    {diagnosisLabels[video.diagnosis] || "Unknown"}
                  </span>
                  {/* <span className="table-cell">
                    {video.diagnosis_probability !== null
                      ? `${(video.diagnosis_probability * 100).toFixed(2)}%`
                      : "NA"}
                  </span> */}
                  <span className="table-cell">
                    {diagnosisLabels[video.doctor_diagnosis] || "Unknown"}
                  </span>
                  <span className="table-cell">
                    {video.allow_for_retraining ? "Yes" : "No"}
                  </span>
                  {/* <span className="table-cell">
                    {video.diagnosis_probability !== null
                      ? `${video.score}`
                      : "NA"}
                  </span> */}
                  <span className="table-cell">
                    {video.status}
                    {isRawVideoUnavailable(video) && " (raw video expired)"}
                  </span>
                  <span className="table-cell video-time">
                    {new Date(video.created_at).toLocaleString()}
                  </span>
                  <span
                    className="table-cell"
                    style={{ display: "flex", gap: "8px" }}
                  >
                    <button
                      className="action-button"
                      onClick={() => handleVideoView(video)}
                      style={{
                        padding: "6px 12px",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "12px",
                      }}
                    >
                      View
                    </button>
                    <button
                      className="action-button delete-video-btn"
                      style={{
                        padding: "6px 12px",
                        color: "white",
                        backgroundColor: "#e74c3c",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "12px",
                      }}
                      onClick={() => handleDeleteVideo(video)}
                    >
                      Delete
                    </button>
                    {video.status === "diagnosis_detected" && (
                      <button
                        className="action-button video-report-btn"
                        onClick={() => generatePDFReport(
                          video,
                          formData.patient_name || patientData?.patient_name || "Unknown Participant",
                        )}
                      >
                        <FaFilePdf /> Report
                      </button>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {showUpload && (
        <div className="profile-modal-overlay" onClick={handleCloseModal}>
          <div
            className="add-video-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="add-video-modal-header">
              <h3>Upload Video</h3>
              <button className="modal-close-btn" onClick={handleCloseModal}>
                <FaTimes />
              </button>
            </div>

            <div className="add-video-modal-body">
              <div className="upload-section">
                <label className="file-input-label">
                  <input
                    type="file"
                    accept="video/mp4"
                    onChange={handleFileChange}
                    disabled={uploading}
                    className="file-input-hidden"
                  />
                  <div className="file-input-button">
                    <FaUpload className="upload-icon" />
                    <span>
                      {selectedFile ? selectedFile.name : "Choose Video File"}
                    </span>
                  </div>
                </label>
                {uploadError && (
                  <div className="upload-error-message">{uploadError}</div>
                )}
              </div>

              <div className="disclaimer-section">
                <h4 className="disclaimer-title">Important Notice</h4>
                <div className="disclaimer-content">
                  <p className="disclaimer-text">
                    <strong>Important:</strong> AI-ASD123 is a health
                    information management tool that uses AI to process,
                    summarise and organise health-related information. It does
                    not provide medical advice, diagnosis, or treatment, and its
                    outputs may be incomplete or inaccurate. Do not use
                    AI-ASD123 to make medical decisions or replace a qualified
                    healthcare professional's judgment.
                  </p>
                  <p className="disclaimer-text">
                    This report is for reference only. If you have concerns
                    regarding your child's development or cognitive function,
                    please contact your clinician in charge.
                  </p>
                  <p className="disclaimer-text">
                    Development in children is a continuous process. As a child
                    grows and develops, different aspects of him or her are
                    changing continuously. Therefore, the information and
                    recommendations contained in this report are meant for
                    current use. Reference to or use of this report in the
                    future should be made with considerable caution and
                    reservation.
                  </p>
                </div>
              </div>

              <div
                style={{
                  marginTop: "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <input
                  id="allow-for-retraining-checkbox"
                  type="checkbox"
                  checked={allowForRetraining}
                  onChange={(e) => setAllowForRetraining(e.target.checked)}
                  disabled={uploading}
                />
                <label
                  htmlFor="allow-for-retraining-checkbox"
                  className="disclaimer-text"
                  style={{ margin: 0, cursor: "pointer" }}
                >
                  I give permission for this video to be used for medical
                  study.
                </label>
              </div>
            </div>

            <div className="add-video-modal-footer">
              <button
                className="modal-cancel-btn"
                onClick={handleCloseModal}
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                className="modal-upload-btn"
                onClick={handleUpload}
                disabled={uploading || !selectedFile}
              >
                {uploading ? (
                  <>
                    <span className="upload-spinner"></span>
                    Uploading...
                  </>
                ) : (
                  <>
                    <FaUpload />
                    Upload Video
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Video Modal */}
      {showVideoModal && (
        <div className="profile-modal-overlay" onClick={handleCloseVideoModal}>
          <div
            className="video-player-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="video-player-modal-header">
              <div className="video-modal-title-section">
                <h3 className="video-player-modal-title">
                  {selectedVideo?.video_name || "Video Player"}
                </h3>
                <div className="video-modal-meta">
                  <span className="video-meta-item">
                    Status:{" "}
                    <span className="video-status-badge">
                      {selectedVideo?.status}
                    </span>
                  </span>
                  {selectedVideo?.created_at && (
                    <span className="video-meta-item">
                      {new Date(selectedVideo.created_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
              <button
                className="video-modal-close-btn"
                onClick={handleCloseVideoModal}
              >
                <FaTimes />
              </button>
            </div>

            <div className="video-player-modal-body">
              {videoLoading && (
                <div className="video-loading-state">
                  <div className="video-loading-spinner"></div>
                  <p>Loading video...</p>
                </div>
              )}

              {videoError && (
                <div className="video-error-state">
                  <p>{videoError}</p>
                  {!selectedRawUnavailable && (
                  <button
                    className="video-retry-btn"
                    onClick={() =>
                      selectedVideo &&
                      requestPlaybackUrl(selectedVideo.video_id, {
                        preserveState: true,
                        showLoading: true,
                      })
                    }
                  >
                    Retry
                  </button>
                  )}
                </div>
              )}

              {videoUrl && !selectedRawUnavailable && !videoLoading && !videoError && (
                <video
                  ref={videoElementRef}
                  controls
                  src={videoUrl}
                  className="video-player-element"
                  crossOrigin="anonymous"
                  onError={handleVideoPlaybackError}
                >
                  Your browser does not support the video tag.
                </video>
              )}
            </div>

            {/* Scrollable Content Container */}
            <div className="video-modal-scrollable-content">
              {/* Video Information Card */}
              {selectedVideo && (
                <div className="video-info-card">
                  <h4 className="video-info-title">Analysis Results</h4>
                  <div className="video-info-grid">
                    <div className="video-info-item">
                      <span className="video-info-label">AI Diagnosis:</span>
                      <span className="video-info-value">
                        {diagnosisLabels[selectedVideo.diagnosis] || "Unknown"}
                      </span>
                    </div>
                    <div className="video-info-item">
                      <span className="video-info-label">Probability:</span>
                      <span className="video-info-value">
                        {selectedVideo.diagnosis_probability !== null
                          ? `${(selectedVideo.diagnosis_probability * 100).toFixed(2)}%`
                          : "N/A"}
                      </span>
                    </div>
                    <div className="video-info-item">
                      <span className="video-info-label">
                        Doctor Diagnosis:
                      </span>
                      <span className="video-info-value">
                        {diagnosisLabels[selectedVideo.doctor_diagnosis] ||
                          "Unknown"}
                      </span>
                    </div>
                    <div className="video-info-item">
                      <span className="video-info-label">CRAS score:</span>
                      <span className="video-info-value">
                        {selectedVideo.score !== null
                          ? `${selectedVideo.score}`
                          : "N/A"}
                      </span>
                    </div>
                    <div className="video-info-item">
                      <span className="video-info-label">Labeled By:</span>
                      <span className="video-info-value">
                        {selectedVideo.users_labeled &&
                        selectedVideo.users_labeled.length > 0
                          ? selectedVideo.users_labeled.join(", ")
                          : "None"}
                      </span>
                    </div>
                    <div className="video-info-item">
                      <span className="video-info-label">Uploaded By:</span>
                      <span className="video-info-value">
                        {selectedVideo.uploaded_by || "Unknown"}
                      </span>
                    </div>
                    <div className="video-info-item">
                      <span className="video-info-label">
                        Allow For Retraining:
                      </span>
                      <span className="video-info-value">
                        <label
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={allowForRetrainingDetail}
                            onChange={(e) =>
                              setAllowForRetrainingDetail(e.target.checked)
                            }
                            disabled={isSubmittingDiagnosis || selectedRawUnavailable}
                          />
                          <span>
                            Given premission for medical study.
                          </span>
                        </label>
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Severity Selection - Only for Admin, ITDOG, and Doctor */}
              {canEditSeverity && (
                <>
                  <div className="video-severity-section">
                    <h4 className="severity-section-title">
                      Update Diagnosis Level
                    </h4>
                    <div className="severity-options-grid">
                      {severityOptions.map((option) => (
                        <label
                          key={option.value}
                          className={
                            "severity-option-card" +
                            (selectedSeverity === option.value
                              ? " selected"
                              : "")
                          }
                        >
                          <input
                            type="radio"
                            name="severity"
                            value={option.value}
                            checked={selectedSeverity === option.value}
                            onChange={(e) =>
                              handleSeverityChange(e.target.value)
                            }
                          />
                          <span className="severity-option-label">
                            {option.label}
                          </span>
                        </label>
                      ))}
                    </div>
                    {/* Score input field on next line */}
                    <div
                      style={{
                        marginTop: 12,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <label
                        htmlFor="score-input"
                        style={{ marginRight: 8, fontWeight: 500 }}
                      >
                        CRAS score (15-60):
                      </label>
                      <input
                        id="score-input"
                        type="number"
                        min={15}
                        max={60}
                        value={score}
                        onChange={(e) => setScore(e.target.value)}
                        style={{ width: 70, padding: 4 }}
                      />
                    </div>
                  </div>

                  {/* Status Messages */}
                  {diagnosisSuccess && (
                    <div className="video-success-message">
                      {diagnosisSuccess}
                    </div>
                  )}
                  {diagnosisError && (
                    <div className="video-error-message">{diagnosisError}</div>
                  )}
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div className="video-player-modal-footer">
              <button
                className="video-report-btn"
                onClick={() =>
                  generatePDFReport(
                    selectedVideo,
                    formData.patient_name ||
                      patientData?.patient_name ||
                      "Unknown Participant",
                  )
                }
                disabled={selectedVideo?.status !== "diagnosis_detected"}
              >
                <FaFilePdf />
                <span>Generate Report</span>
              </button>

              {canEditSeverity && (
                <button
                  className="video-submit-btn"
                  onClick={handleSubmitDiagnosis}
                  disabled={
                    isSubmittingDiagnosis ||
                    (selectedSeverity === "" &&
                      allowForRetrainingDetail ===
                        initialAllowForRetrainingDetail)
                  }
                >
                  {isSubmittingDiagnosis ? (
                    <>
                      <span className="upload-spinner"></span>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <FaSave />
                      Submit Diagnosis
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ProfileDetail = ({
  mode = "create",
  patientId = null,
  onSave,
  onCancel,
  onDelete,
}) => {
  const [activeTab, setActiveTab] = useState("personal");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [patientData, setPatientData] = useState(null);
  const [existingReferencePhotos, setExistingReferencePhotos] = useState([]);
  const [removedExistingPhotos, setRemovedExistingPhotos] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [forceRefresh, setForceRefresh] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    formData,
    handleChange,
    handleFileRemove,
    resetForm,
    setFormDataDirectly,
    hasChanges,
  } = useFormData({ ...INITIAL_FORM_DATA, patient_dob: getTodayDateString() });

  const { errors, validateField, validateAll, clearErrors, setErrors } =
    useFormValidation(formData);

  const triggerImageRefresh = useCallback(() => {
    setForceRefresh((prev) => prev + 1);
  }, []);

  useEffect(() => {
    const loadPatientData = async () => {
      if (mode === "edit" && patientId) {
        try {
          setIsLoading(true);
          setLoadError(null);
          const patient = await fetchPatientById(patientId);
          setPatientData(patient);
          const existingRefPhotos = Array.isArray(
            patient.patient_reference_pics,
          )
            ? patient.patient_reference_pics
            : [];
          setExistingReferencePhotos(existingRefPhotos);
          const mappedFormData = {
            patient_name: patient.patient_name || "",
            patient_email: patient.patient_email || "",
            patient_phone: patient.patient_phone || "",
            patient_dob: formatDateForInput(patient.patient_dob),
            patient_profile_pic: patient.patient_profile_pic || null,
            patient_reference_pics: [],
          };
          setFormDataDirectly(mappedFormData);
          setForceRefresh((prev) => prev + 1);
        } catch (error) {
          console.error("Error loading patient data:", error);
          setLoadError("Failed to load participant data");
        } finally {
          setIsLoading(false);
        }
      }
    };
    loadPatientData();
  }, [mode, patientId, setFormDataDirectly]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(""), 8000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  const handleFieldChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      handleChange(e);
      if (!e.target.files) {
        validateField(name, value);
      }
    },
    [handleChange, validateField],
  );

  const handleExistingPhotoRemove = useCallback((index, photoPath) => {
    setRemovedExistingPhotos((prev) => [...prev, photoPath]);
    setExistingReferencePhotos((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const isFormValid = () => {
    // Check if there are any errors
    if (Object.keys(errors).length > 0) {
      return false;
    }

    // Only patient_name is required
    if (
      !formData["patient_name"] ||
      formData["patient_name"].toString().trim() === ""
    ) {
      return false;
    }

    return true;
  };

  const uploadReferencePhotos = async (patientIdParam, referencePhotos) => {
    if (!referencePhotos || referencePhotos.length === 0) {
      return { success: true, errors: [] };
    }
    const filePhotos = referencePhotos.filter((photo) => photo instanceof File);
    if (filePhotos.length === 0) {
      return { success: true, errors: [] };
    }
    const uploadErrors = [];
    let successCount = 0;
    for (let i = 0; i < filePhotos.length; i++) {
      const photo = filePhotos[i];
      try {
        await uploadPatientReferencePic(patientIdParam, photo);
        successCount++;
      } catch (uploadError) {
        console.error(`Error uploading reference photo ${i + 1}:`, uploadError);
        uploadErrors.push({
          index: i + 1,
          fileName: photo.name,
          error: uploadError.message || "Upload failed",
        });
      }
    }
    return {
      success: uploadErrors.length === 0,
      successCount,
      totalCount: filePhotos.length,
      errors: uploadErrors,
    };
  };

  const handleSubmit = useCallback(
    async (e) => {
      e?.preventDefault();
      setIsSubmitting(true);
      setSuccessMessage("");
      setErrorMessage("");
      try {
        let profilePicShouldInclude = true;
        let referencePicsShouldInclude = true;
        if (mode === "edit" && patientData) {
          if (
            !formData.patient_profile_pic ||
            (formData.patient_profile_pic &&
              formData.patient_profile_pic instanceof File)
          ) {
            profilePicShouldInclude = false;
          }
          referencePicsShouldInclude = true;
        }
        let patient_profile_pic = null;
        if (profilePicShouldInclude) {
          if (typeof formData.patient_profile_pic === "string") {
            patient_profile_pic = formData.patient_profile_pic;
          }
        }
        let patient_reference_pics = [];
        if (referencePicsShouldInclude) {
          if (Array.isArray(existingReferencePhotos)) {
            patient_reference_pics = existingReferencePhotos.filter(
              (p) => typeof p === "string",
            );
          }
        }
        const submitData = {
          patient_name: formData.patient_name,
          ...(formData.patient_email
            ? { patient_email: formData.patient_email }
            : {}),
          ...(formData.patient_phone
            ? { patient_phone: formData.patient_phone }
            : {}),
          ...(formData.patient_dob
            ? { patient_dob: formatDateForAPI(formData.patient_dob) }
            : {}),
          ...(profilePicShouldInclude ? { patient_profile_pic } : {}),
          ...(referencePicsShouldInclude ? { patient_reference_pics } : {}),
        };
        let result;
        let currentPatientId;
        let profilePicUploaded = false;
        let referencePicsUploaded = false;
        if (mode === "create") {
          result = await createPatient(submitData);
          currentPatientId = result.patient_id;
          if (
            formData.patient_profile_pic &&
            formData.patient_profile_pic instanceof File
          ) {
            try {
              await uploadPatientProfilePic(
                currentPatientId,
                formData.patient_profile_pic,
              );
              profilePicUploaded = true;
            } catch (uploadError) {
              console.error("Error uploading profile picture:", uploadError);
              setErrorMessage(
                "Participant created successfully, but profile picture upload failed. You can try uploading it again later.",
              );
              resetForm();
              await onSave?.(result);
              return;
            }
          }
          if (
            formData.patient_reference_pics &&
            formData.patient_reference_pics.length > 0
          ) {
            try {
              const referenceUploadResult = await uploadReferencePhotos(
                currentPatientId,
                formData.patient_reference_pics,
              );
              if (!referenceUploadResult.success) {
                const errorMessages = referenceUploadResult.errors
                  .map(
                    (err) =>
                      `Photo ${err.index} (${err.fileName}): ${err.error}`,
                  )
                  .join(", ");
                setErrorMessage(
                  `Participant created successfully, but some reference photos failed to upload: ${errorMessages}. You can try uploading them again later.`,
                );
                resetForm();
                await onSave?.(result);
                return;
              } else {
                referencePicsUploaded = true;
              }
            } catch (uploadError) {
              console.error("Error uploading reference photos:", uploadError);
              setErrorMessage(
                "Participant created successfully, but reference photos upload failed. You can try uploading them again later.",
              );
              resetForm();
              await onSave?.(result);
              return;
            }
          }
          setSuccessMessage("Participant created successfully!");
          resetForm();
        } else {
          currentPatientId = patientId;
          result = await updatePatient(currentPatientId, submitData);
          if (
            formData.patient_profile_pic &&
            formData.patient_profile_pic instanceof File
          ) {
            try {
              if (typeof clearImageCache === "function") {
                clearImageCache(currentPatientId);
              }
              await uploadPatientProfilePic(
                currentPatientId,
                formData.patient_profile_pic,
              );
              profilePicUploaded = true;
            } catch (uploadError) {
              console.error("Error uploading profile picture:", uploadError);
              setErrorMessage(
                "Participant updated successfully, but profile picture upload failed. You can try uploading it again later.",
              );
              await onSave?.(result);
              return;
            }
          }
          if (
            formData.patient_reference_pics &&
            formData.patient_reference_pics.length > 0
          ) {
            try {
              if (typeof clearImageCache === "function") {
                clearImageCache(currentPatientId);
              }
              const referenceUploadResult = await uploadReferencePhotos(
                currentPatientId,
                formData.patient_reference_pics,
              );
              if (!referenceUploadResult.success) {
                const errorMessages = referenceUploadResult.errors
                  .map(
                    (err) =>
                      `Photo ${err.index} (${err.fileName}): ${err.error}`,
                  )
                  .join(", ");
                setErrorMessage(
                  `Participant updated successfully, but some reference photos failed to upload: ${errorMessages}. You can try uploading them again later.`,
                );
                await onSave?.(result);
                return;
              } else if (referenceUploadResult.successCount > 0) {
                referencePicsUploaded = true;
              }
            } catch (uploadError) {
              console.error("Error uploading reference photos:", uploadError);
              setErrorMessage(
                "Participant updated successfully, but reference photos upload failed. You can try uploading them again later.",
              );
              await onSave?.(result);
              return;
            }
          }
          if (removedExistingPhotos.length > 0) {
            if (typeof clearImageCache === "function") {
              clearImageCache(currentPatientId);
            }
          }
          if (profilePicUploaded || referencePicsUploaded) {
            try {
              await new Promise((resolve) => setTimeout(resolve, 1000));
              const updatedPatient = await fetchPatientById(currentPatientId);
              const updatedRefPhotos = Array.isArray(
                updatedPatient.patient_reference_pics,
              )
                ? updatedPatient.patient_reference_pics
                : [];
              setExistingReferencePhotos(updatedRefPhotos);
              const updatedFormData = {
                ...formData,
                patient_profile_pic: updatedPatient.patient_profile_pic || null,
                patient_reference_pics: [],
              };
              setFormDataDirectly(updatedFormData);
              triggerImageRefresh();
            } catch (reloadError) {
              console.error("Error reloading patient data:", reloadError);
              triggerImageRefresh();
            }
          }
          setSuccessMessage("Participant updated successfully!");
        }
        await onSave?.(result);
        clearErrors();
      } catch (error) {
        console.error("Error saving patient:", error);
        let errorMsg = `Failed to ${mode} patient. Please try again.`;
        if (error.response?.status === 404) {
          errorMsg = "Participant not found. Please refresh and try again.";
        } else if (error.response?.status === 400) {
          errorMsg = "Invalid patient data. Please check your inputs.";
        } else if (error.response?.status === 401) {
          errorMsg = "You are not authorized to update this patient.";
        } else if (error.response?.status === 422) {
          errorMsg = "Validation error. Please check your input data.";
        } else if (error.response?.data?.detail) {
          if (Array.isArray(error.response.data.detail)) {
            errorMsg = error.response.data.detail
              .map((err) => err.msg || err.message || err)
              .join(", ");
          } else {
            errorMsg = error.response.data.detail;
          }
        } else if (error.response?.data?.message) {
          errorMsg = error.response.data.message;
        } else if (error.message) {
          errorMsg = error.message;
        }
        setErrorMessage(errorMsg);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      formData,
      onSave,
      validateAll,
      clearErrors,
      resetForm,
      mode,
      patientId,
      removedExistingPhotos,
      triggerImageRefresh,
      setFormDataDirectly,
      patientData,
      existingReferencePhotos,
    ],
  );

  const handleDeleteClick = useCallback(() => {
    setShowDeleteModal(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!patientData?.patient_id) return;
    setIsDeleting(true);
    try {
      await deletePatient(patientData.patient_id);
      setShowDeleteModal(false);
      onDelete?.();
    } catch (error) {
      setShowDeleteModal(false);
      let errorMsg = "Failed to delete participant";
      if (error.response?.data?.detail) {
        errorMsg =
          typeof error.response.data.detail === "string"
            ? error.response.data.detail
            : error.response.data.detail.map((e) => e.msg || e).join(", ");
      } else if (error.message) {
        errorMsg = error.message;
      }
      setErrorMessage(errorMsg);
    } finally {
      setIsDeleting(false);
    }
  }, [patientData, onDelete]);

  const handleCancel = useCallback(() => {
    if (hasChanges || removedExistingPhotos.length > 0) {
      const confirmCancel = window.confirm(
        "You have unsaved changes. Are you sure you want to cancel?",
      );
      if (!confirmCancel) return;
    }
    resetForm();
    clearErrors();
    setSuccessMessage("");
    setErrorMessage("");
    setRemovedExistingPhotos([]);
    onCancel?.();
  }, [
    hasChanges,
    resetForm,
    clearErrors,
    onCancel,
    removedExistingPhotos.length,
  ]);

  const renderActiveForm = () => {
    const commonProps = {
      formData,
      onChange: handleFieldChange,
      onFileRemove: handleFileRemove,
      errors,
      patientId,
      forceRefresh,
    };
    switch (activeTab) {
      case "personal":
        return <PersonalInformationForm {...commonProps} />;
      case "photos":
        return (
          <PhotosForm
            {...commonProps}
            existingReferencePhotos={existingReferencePhotos}
            onExistingPhotoRemove={handleExistingPhotoRemove}
          />
        );
      case "videos":
        return (
          <VideoListForm
            patientId={patientId}
            patientData={patientData}
            formData={formData}
          />
        );
      default:
        return <PersonalInformationForm {...commonProps} />;
    }
  };

  if (isLoading) {
    return (
      <div className="view">
        <div className="page-header">
          <h2>Loading Participant Data...</h2>
        </div>
        <div className="loading-container">
          <div className="loading-spinner">Loading...</div>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="view">
        <div className="page-header">
          <h2>Error</h2>
        </div>
        <div className="error-container">
          <p>{loadError}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="view">
      {showDeleteModal && (
        <DeleteConfirmModal
          onConfirm={handleDeleteConfirm}
          onCancel={() => setShowDeleteModal(false)}
          isDeleting={isDeleting}
        />
      )}
      {successMessage && (
        <SuccessMessage
          message={successMessage}
          onClose={() => setSuccessMessage("")}
        />
      )}
      {errorMessage && (
        <ErrorMessage
          message={errorMessage}
          onClose={() => setErrorMessage("")}
        />
      )}
      <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />
      <div className="contain">{renderActiveForm()}</div>
      <ActionButtons
        onSave={handleSubmit}
        onCancel={handleCancel}
        onDelete={mode === "edit" ? handleDeleteClick : undefined}
        hasChanges={hasChanges || removedExistingPhotos.length > 0}
        isSubmitting={isSubmitting}
        mode={mode}
        isFormValid={isFormValid()}
      />
    </div>
  );
};

export default ProfileDetail;
